import React, { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import type { ListingImageData } from '../../../../../hooks/useListingImageUpload';
import { UNSUPPORTED_IMAGE_ERROR } from '../../../../../hooks/useListingImageUpload';
import EstimateInfoTooltip from '../../../../common/EstimateInfoTooltip/EstimateInfoTooltip';
import { PlusIcon, InfoIcon, EditIcon, ClearIcon, ExpandIcon } from '../icons';
import {
  AddMyPlaceFormState,
  BuildingTypeValue,
  clampPropertyFloorInput,
  clampPropertySizeInput,
  MAX_PROPERTY_DESCRIPTION_LENGTH,
  MIN_ASKING_PRICE,
  RenovationStatus,
} from '../types';
import EdgeFadeScroller from '../../../../common/EdgeFadeScroller/EdgeFadeScroller';
import { formatCardPrice } from '../../../../ListingCard/cardUtils';

const HOUSE_IMG = `${process.env.PUBLIC_URL}/assets/markers/seller/Home.png`;
const JAAKKO_AVATAR = `${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`;

// Group a raw digit string into thousands separated by spaces for readable
// prices (e.g. "5590000" -> "5 590 000"). State keeps the unformatted digits.
const formatThousands = (digits: string) =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

// Delay before the white "see the enhanced photos" tip animates in after the
// first photos are added, so the user has time to refocus on the app once the
// browser's file picker closes.
const ENHANCED_TIP_DELAY_MS = 900;

interface ReadyToPublishStepProps {
  form: AddMyPlaceFormState;
  displayAddress: string;
  previewCity: string;
  // Street + "postal city" (postal before the city); shown in the address
  // detail field. The listing card keeps the plain street + city form.
  addressWithPostal: string;
  displaySize: string;
  displayRooms: string;
  onBuildingTypeChange: (value: BuildingTypeValue) => void;
  onFormChange?: (updates: Partial<AddMyPlaceFormState>) => void;
  onPublish: () => void;
  // True while the publish request is in flight; disables the CTA and shows a
  // spinner so the user can't double-submit.
  isPublishing?: boolean;
  // The "Add more details" expanded state lives in the flow root so the
  // desktop modal can grow to full height in sync with the details reveal.
  detailsExpanded: boolean;
  onDetailsExpandedChange: (expanded: boolean) => void;
  photos?: AmpPhotoGridPhoto[];
  uploadError?: string;
  onUploadPhotos?: (files: FileList) => void;
  onRemovePhoto?: (index: number) => void;
  onReorderPhotos?: (fromIndex: number, toIndex: number) => void;
  onOpenPhotoManager?: (photoIndex: number) => void;
  hasSeenPhotoLightbox?: boolean;
  isLoggedIn: boolean;
  setLoginModalVisible: (isVisible: boolean) => void;
  // Footer CTA label, defaulting to the "Publish" wizard label. The edit modal
  // passes "Update"; the estimate flow passes "Get estimate".
  ctaLabel?: string;
  // Overrides the step title (defaults to "Nice, ready to publish?"); the
  // estimate flow passes its own.
  title?: string;
  // Overrides the "Add details and photos" hint; the estimate tool has no listing to promote (JAA-369).
  detailsHint?: string;
  // Hides the asking price field and its tip; the estimate tool estimates the price, it never asks for one (JAA-369).
  hidePrice?: boolean;
  // The "estimate ready" notify banner is create-only; other hosts hide it.
  // Create is also the only host that shows the "let Jaakko estimate" price tip
  // (JAA-369), so the flag doubles as the create marker for that choice.
  showEstimateNotify?: boolean;
  // Optional edit affordance (pencil on the card) that re-runs the edit flow
  // (building -> size -> rooms -> back here). The inline size/rooms fields stay
  // available alongside it, so re-picking the address is a way to change those
  // facts rather than the only way. Create leaves it undefined.
  onEditFacts?: () => void;
  // A completed estimate for the listing (edit flow, JAA-329 #15): shown as
  // "~697 500 €" in place of the "Automatic price estimate" placeholder while
  // no asking price is set. Create leaves these undefined.
  estimatedPrice?: number | null;
  estimatedPriceCurrency?: string | null;
  // 'pending' swaps the price tip to the "Jaakko is estimating" copy (JAA-369).
  estimateStatus?: 'pending' | 'completed' | 'failed' | null;
}

type AmpPhotoGridPhoto = ListingImageData & {
  alt?: string;
  kind?: 'photo' | 'floorplan' | 'document';
};

type AmpPhotoGridProps = {
  photos: AmpPhotoGridPhoto[];
  maxPhotos?: number;
  onAddPhoto?: () => void;
  onRemovePhoto?: (index: number) => void;
  onReorderPhotos?: (fromIndex: number, toIndex: number) => void;
  onOpenPhotoManager?: (photoIndex: number) => void;
  t: ReturnType<typeof useTranslation>['t'];
};

const getOrderedPhotos = (photos: AmpPhotoGridPhoto[]) => [...photos];
const getPhotoKey = (photo: AmpPhotoGridPhoto, index: number) =>
  photo.id || `photo-${index}`;

type SortableAmpPhotoTileProps = {
  photo: AmpPhotoGridPhoto;
  index: number;
  isCover: boolean;
  onRemovePhoto?: (index: number) => void;
  onOpenPhotoManager?: (photoIndex: number) => void;
  t: ReturnType<typeof useTranslation>['t'];
};

/**
 * One sortable photo tile. A refused upload says why (unsupported format) instead of a generic failure,
 * and its tile drops the cover pill and expand button so the message is not covered (Publish drops the
 * failed photo, so it is never the cover).
 */
const SortableAmpPhotoTile = ({
  photo,
  index,
  isCover,
  onRemovePhoto,
  onOpenPhotoManager,
  t,
}: SortableAmpPhotoTileProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: getPhotoKey(photo, index),
    disabled: photo.isUploading,
  });
  const src = photo.thumbnailUrl || photo.url;
  const isNonPhotoAsset = photo.kind != null && photo.kind !== 'photo';
  const isEnhancedActive = Boolean(
    photo.enhancedUrl && photo.url === photo.enhancedUrl,
  );
  const canOpen = !photo.isUploading && !photo.error;

  const openPhoto = () => {
    if (canOpen) onOpenPhotoManager?.(index);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={`amp-photo-thumb${isNonPhotoAsset ? ' amp-photo-thumb--contain' : ''}${isDragging ? ' amp-photo-thumb--dragging' : ''}${photo.error ? ' amp-photo-thumb--error' : ''}`}
      role={canOpen ? 'button' : 'img'}
      tabIndex={canOpen ? 0 : undefined}
      onClick={openPhoto}
      onKeyDown={(event) => {
        if (!canOpen) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openPhoto();
        }
      }}
      aria-label={t('newLandingPage.addMyPlace.details.openPhoto', {
        number: index + 1,
      })}
    >
      <img
        src={src}
        alt={
          photo.alt ||
          t('newLandingPage.addMyPlace.details.photoAlt', { number: index + 1 })
        }
        className="amp-photo-thumb__img"
        draggable={false}
      />

      {!photo.error && (
        <button
          type="button"
          className="amp-photo-thumb__expand"
          onClick={(event) => {
            event.stopPropagation();
            openPhoto();
          }}
          aria-label={t('newLandingPage.addMyPlace.details.openPhoto', {
            number: index + 1,
          })}
          disabled={!canOpen}
        >
          <ExpandIcon fillColor="#8e9ca3" />
        </button>
      )}

      {isCover && !photo.error && (
        <span className="amp-photo-thumb__cover">
          {t('building.offMarket.modal.coverPhoto')}
        </span>
      )}

      {photo.enhancedUrl && !photo.isUploading && !photo.error && (
        <span
          className={`amp-photo-thumb__version${isEnhancedActive ? ' amp-photo-thumb__version--enhanced' : ''}`}
        >
          {isEnhancedActive
            ? t('building.offMarket.modal.enhanced')
            : t('building.offMarket.modal.original')}
        </span>
      )}

      {photo.isUploading &&
        (photo.isEnhancing ? (
          <span className="amp-photo-thumb__state amp-photo-thumb__state--enhancing">
            <span
              className="amp-photo-thumb__enhance-sheen"
              aria-hidden="true"
            />
            <span className="amp-photo-thumb__enhance-content">
              <span
                className="amp-photo-thumb__enhance-sparkle"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M11.519 2.364a.5.5 0 0 1 .962 0l1.583 6.135A2 2 0 0 0 15.5 9.936l6.135 1.583a.5.5 0 0 1 0 .962L15.5 14.064a2 2 0 0 0-1.436 1.437l-1.583 6.135a.5.5 0 0 1-.962 0L9.936 15.5A2 2 0 0 0 8.5 14.064l-6.135-1.583a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.936 8.5l1.583-6.135Z" />
                </svg>
              </span>
              <span className="amp-photo-thumb__enhance-label">
                {t('newLandingPage.addMyPlace.details.photoEnhancing')}
              </span>
            </span>
          </span>
        ) : (
          <span className="amp-photo-thumb__state">
            {t('newLandingPage.addMyPlace.details.photoUploading')}
          </span>
        ))}

      {photo.error && (
        <span className="amp-photo-thumb__state amp-photo-thumb__state--error">
          {t(
            photo.error === UNSUPPORTED_IMAGE_ERROR
              ? 'building.offMarket.modal.unsupportedImage'
              : 'building.offMarket.modal.uploadFailed',
          )}
        </span>
      )}

      <button
        type="button"
        className="amp-photo-thumb__remove"
        onClick={(event) => {
          event.stopPropagation();
          onRemovePhoto?.(index);
        }}
        aria-label={t('building.offMarket.modal.removeImage')}
      >
        <ClearIcon />
      </button>

      <button
        ref={setActivatorNodeRef}
        type="button"
        className="amp-photo-thumb__drag"
        onClick={(event) => event.stopPropagation()}
        aria-label={t('building.offMarket.modal.dragToReorder')}
        {...attributes}
        {...listeners}
      >
        <svg
          fill="none"
          height="512"
          viewBox="0 0 24 24"
          width="512"
          xmlns="http://www.w3.org/2000/svg"
          id="fi_8105943"
        >
          <g fill="#222">
            <path d="m8.5 7c1.10457 0 2-.89543 2-2s-.89543-2-2-2-2 .89543-2 2 .89543 2 2 2z"></path>
            <path d="m8.5 14c1.10457 0 2-.8954 2-2s-.89543-2-2-2-2 .8954-2 2 .89543 2 2 2z"></path>
            <path d="m10.5 19c0 1.1046-.89543 2-2 2s-2-.8954-2-2 .89543-2 2-2 2 .8954 2 2z"></path>
            <path d="m15.5 7c1.1046 0 2-.89543 2-2s-.8954-2-2-2-2 .89543-2 2 .8954 2 2 2z"></path>
            <path d="m17.5 12c0 1.1046-.8954 2-2 2s-2-.8954-2-2 .8954-2 2-2 2 .8954 2 2z"></path>
            <path d="m15.5 21c1.1046 0 2-.8954 2-2s-.8954-2-2-2-2 .8954-2 2 .8954 2 2 2z"></path>
          </g>
        </svg>
      </button>
    </div>
  );
};

const AmpPhotoGrid = ({
  photos,
  maxPhotos = 30,
  onAddPhoto,
  onRemovePhoto,
  onReorderPhotos,
  onOpenPhotoManager,
  t,
}: AmpPhotoGridProps) => {
  const orderedPhotos = getOrderedPhotos(photos);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const sortableItems = orderedPhotos.map(getPhotoKey);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortableItems.findIndex((item) => item === active.id);
    const newIndex = sortableItems.findIndex((item) => item === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorderPhotos?.(oldIndex, newIndex);
  };

  // With no photos yet, match the compact 56px "Add ..." row layout used by the
  // other empty fields (icon left, label right) instead of the tall 4:3 grid.
  if (orderedPhotos.length === 0) {
    return (
      <div className="amp-photos-row">
        <button type="button" className="amp-detail-add" onClick={onAddPhoto}>
          <span className="amp-detail-add__icon">
            <PlusIcon />
          </span>
          <span className="amp-detail-add__label">
            {t('newLandingPage.addMyPlace.details.addPhoto')}
          </span>
        </button>
        <div className="amp-photo-slot" aria-hidden="true" />
      </div>
    );
  }

  const tiles: Array<
    | { type: 'photo'; photo: AmpPhotoGridPhoto; index: number }
    | { type: 'add' }
    | { type: 'ghost' }
  > = orderedPhotos.map((photo, index) => ({
    type: 'photo',
    photo,
    index,
  }));

  if (orderedPhotos.length < maxPhotos) {
    tiles.push({ type: 'add' });
  }

  if (tiles.length % 2 === 1) {
    tiles.push({ type: 'ghost' });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
        <div className="amp-photos-row amp-photos-row--grid">
          {tiles.map((tile, index) => {
            if (tile.type === 'photo') {
              return (
                <SortableAmpPhotoTile
                  key={getPhotoKey(tile.photo, tile.index)}
                  photo={tile.photo}
                  index={tile.index}
                  isCover={tile.index === 0}
                  onRemovePhoto={onRemovePhoto}
                  onOpenPhotoManager={onOpenPhotoManager}
                  t={t}
                />
              );
            }

            if (tile.type === 'add') {
              return (
                <button
                  key={`add-${index}`}
                  type="button"
                  className="amp-detail-add amp-detail-add--photo-tile"
                  onClick={onAddPhoto}
                >
                  <span className="amp-detail-add__icon">
                    <PlusIcon />
                  </span>
                  <span className="amp-detail-add__label">
                    {t('newLandingPage.addMyPlace.details.addPhoto')}
                  </span>
                </button>
              );
            }

            return (
              <div
                key={`ghost-${index}`}
                className="amp-photo-slot amp-photo-slot--grid"
                aria-hidden="true"
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};

/**
 * The details and photos step shared by the create (AddMyPlaceFlow), edit
 * (EditListingModal) and estimate hosts. Its CTA holds back a typed asking
 * price under MIN_ASKING_PRICE instead of calling onPublish (JAA-458).
 */
const ReadyToPublishStep = ({
  form,
  displayAddress,
  previewCity,
  addressWithPostal,
  displaySize,
  displayRooms,
  onBuildingTypeChange,
  onFormChange,
  onPublish,
  isPublishing,
  detailsExpanded: expanded,
  onDetailsExpandedChange,
  photos: controlledPhotos,
  uploadError,
  onUploadPhotos,
  onRemovePhoto,
  onReorderPhotos,
  onOpenPhotoManager,
  hasSeenPhotoLightbox = false,
  isLoggedIn,
  setLoginModalVisible,
  ctaLabel,
  title,
  detailsHint,
  hidePrice = false,
  showEstimateNotify = true,
  onEditFacts,
  estimatedPrice,
  estimatedPriceCurrency,
  estimateStatus,
}: ReadyToPublishStepProps) => {
  const { t } = useTranslation();
  // The already-completed estimate label ("~697 500 €"), or null while none
  // exists — the estimate placeholder copy then stays in place.
  const completedEstimate =
    estimateStatus === 'completed' && typeof estimatedPrice === 'number'
      ? `~${formatCardPrice(estimatedPrice, estimatedPriceCurrency ?? 'EUR')}`
      : null;
  // Only one optional detail field is open (in its editing state) at a time.
  const [openField, setOpenField] = useState<string | null>(null);
  const openFieldRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // The white "see the enhanced photos" tip fades in only after a short delay,
  // giving the user time to move their eyes back to the app after the browser's
  // file picker closes (so they actually notice the tip appear/animate).
  const photoCount = (controlledPhotos ?? []).length;
  const shouldShowEnhancedTip = photoCount > 0 && !hasSeenPhotoLightbox;
  const [enhancedTipVisible, setEnhancedTipVisible] = useState(false);

  useEffect(() => {
    if (!shouldShowEnhancedTip) {
      setEnhancedTipVisible(false);
      return undefined;
    }
    const timer = setTimeout(
      () => setEnhancedTipVisible(true),
      ENHANCED_TIP_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [shouldShowEnhancedTip]);

  // Clicking outside the open field's inputs/selections settles it into its
  // resting state (filled if it has a value, empty otherwise). Uses mousedown
  // so taps collapse the field without scroll gestures triggering it.
  useEffect(() => {
    if (!openField) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        openFieldRef.current &&
        !openFieldRef.current.contains(event.target as Node)
      ) {
        setOpenField(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openField]);

  const photos = controlledPhotos ?? [];
  // Any photo still uploading or being enhanced blocks publishing below.
  const photosBusy = photos.some(
    (photo) => photo.isUploading || photo.isEnhancing,
  );
  const ctaBusy = isPublishing || photosBusy;
  // A typed asking price under the minimum (JAA-458); empty is "no set price" and passes.
  const priceTooLow =
    !hidePrice &&
    form.askingPrice.trim() !== '' &&
    Number(form.askingPrice) < MIN_ASKING_PRICE;
  const priceErrorRef = useRef<HTMLDivElement>(null);

  /**
   * The Publish / Update CTA. A price under MIN_ASKING_PRICE is never sent:
   * the details panel opens, the price field closes and the inline error under
   * it scrolls into view, so the CTA never looks dead while the price is off
   * screen. Every host saves through here, so this one guard covers create
   * (AddMyPlaceFlow) and edit (EditListingModal).
   */
  const handlePublishClick = () => {
    if (!priceTooLow) {
      onPublish();
      return;
    }
    onDetailsExpandedChange(true);
    setOpenField(null);
    // ponytail: fixed delay outlasts the 220-300 ms panel expand (.amp-details__content-wrap); use onTransitionEnd if it ever lands short.
    window.setTimeout(
      () =>
        priceErrorRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }),
      expanded ? 0 : 320,
    );
  };
  const coverPhoto = getOrderedPhotos(photos)[0];

  const handleAddPhoto = () => {
    if (!isLoggedIn) {
      setLoginModalVisible(true);
      return;
    }

    photoInputRef.current?.click();
  };

  const handlePhotoInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      onUploadPhotos?.(event.target.files);
    }
    event.target.value = '';
  };

  const buildingTypes: { value: BuildingTypeValue; label: string }[] = [
    {
      value: 'apartment_building',
      label: t('newLandingPage.addMyPlace.building.typeApartment'),
    },
    {
      value: 'terraced_house',
      label: t('newLandingPage.addMyPlace.building.typeRowHouse'),
    },
    {
      value: 'duplex',
      label: t('newLandingPage.addMyPlace.building.typeDuplex'),
    },
    {
      value: 'detached_house',
      label: t('newLandingPage.addMyPlace.building.typeDetached'),
    },
    {
      value: 'other',
      label: t('newLandingPage.addMyPlace.building.typeOther'),
    },
  ];

  const updateForm = (updates: Partial<AddMyPlaceFormState>) =>
    onFormChange?.(updates);

  const renovationOptions: { value: RenovationStatus; label: string }[] = [
    {
      value: 'recently_renovated',
      label: t('newLandingPage.addMyPlace.details.renovationRecentlyRenovated'),
    },
    {
      value: 'good_condition',
      label: t('newLandingPage.addMyPlace.details.renovationGoodCondition'),
    },
    {
      value: 'needs_renovation',
      label: t('newLandingPage.addMyPlace.details.renovationNeedsRenovation'),
    },
  ];

  const yesNoOptions: { value: boolean; label: string }[] = [
    { value: true, label: t('newLandingPage.addMyPlace.details.optionYes') },
    { value: false, label: t('newLandingPage.addMyPlace.details.optionNo') },
  ];

  const labelFor = <T,>(
    options: { value: T; label: string }[],
    value: T | null,
  ) => options.find((option) => option.value === value)?.label ?? '';

  // Empty-state "add" row (beige tile, yellow plus) that opens a field's editor.
  const renderAddRow = (key: string, label: string) => (
    <button
      key={key}
      type="button"
      className="amp-detail-add"
      onClick={() => setOpenField(key)}
    >
      <span className="amp-detail-add__icon">
        <PlusIcon />
      </span>
      <span className="amp-detail-add__label">{label}</span>
    </button>
  );

  // Stacked (renovation) or wrapping (elevator, balcony) choice pills. Selecting
  // an option records it and collapses the field back to its filled state.
  const renderChoiceEditing = <T,>(
    options: { value: T; label: string }[],
    selected: T | null,
    onSelect: (value: T) => void,
    stacked = false,
  ) => (
    <div className={`amp-pills amp-pills--${stacked ? 'stack' : 'grid'}`}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={`amp-pill${stacked ? ' amp-pill--block' : ''}${selected === option.value ? ' amp-pill--selected' : ''}`}
          onClick={() => {
            onSelect(option.value);
            setOpenField(null);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  // Generic inline progressive-disclosure field: empty add row -> editing ->
  // filled summary (which reopens the editor on tap). Fields that always carry
  // a value (size, rooms) set `alwaysField` so they render the collapsed
  // summary instead of the beige add row.
  const renderOptionalField = (config: {
    key: string;
    emptyLabel?: string;
    fieldLabel: string;
    hasValue: boolean;
    displayValue?: string;
    clampValue?: boolean;
    inline?: boolean;
    alwaysField?: boolean;
    estimate?: boolean;
    renderExtra?: () => React.ReactNode;
    renderEditing: () => React.ReactNode;
  }) => {
    const {
      key,
      emptyLabel,
      fieldLabel,
      hasValue,
      displayValue,
      clampValue,
      inline,
      alwaysField,
      estimate,
      renderExtra,
      renderEditing,
    } = config;
    const isEditing = openField === key;

    if (!isEditing && !hasValue && !alwaysField) {
      return renderAddRow(key, emptyLabel ?? fieldLabel);
    }

    // Show the estimate styling only while the field is still on its estimate
    // placeholder (no user-entered value).
    const showEstimate = estimate && !hasValue;

    return (
      <div
        className={`amp-detail-field${inline ? ' amp-detail-field--inline' : ''}`}
        key={key}
        ref={isEditing ? openFieldRef : undefined}
      >
        <span className="amp-detail-field__label">{fieldLabel}</span>
        {isEditing ? (
          renderEditing()
        ) : (
          <button
            type="button"
            className="amp-detail-field__box amp-detail-field__box--button"
            onClick={() => setOpenField(key)}
          >
            <span
              className={`amp-detail-field__value${clampValue ? ' amp-detail-field__value--clamp' : ''}${showEstimate ? ' amp-detail-field__value--estimate' : ''}`}
            >
              {displayValue}
              {showEstimate && (
                <img
                  src={JAAKKO_AVATAR}
                  alt=""
                  aria-hidden="true"
                  className="amp-detail-field__estimate-avatar"
                />
              )}
            </span>
            <span className="amp-detail-field__edit">
              <EditIcon />
            </span>
          </button>
        )}
        {renderExtra?.()}
      </div>
    );
  };

  // The price tip's key prefix: "estimating" while the listing's estimate runs
  // (edit), the "let Jaakko estimate" copy on create, where an empty price is
  // what the published listing shows the estimate for, else the neutral
  // "already have a price?" copy (edit, estimate tool).
  const priceTipKeyPrefix = () => {
    if (estimateStatus === 'pending')
      return 'newLandingPage.addMyPlace.details.estimatingTip';
    if (showEstimateNotify)
      return 'newLandingPage.addMyPlace.details.priceEstimateTip';
    return 'newLandingPage.addMyPlace.details.priceTip';
  };

  // Jaakko tip with a bold first line (heading) followed by the body copy.
  const renderTip = ({
    strongKey,
    restKey,
    whiteBackground,
    compact = false,
    animateIn = false,
  }: {
    strongKey: string;
    restKey: string;
    whiteBackground?: boolean;
    compact?: boolean;
    animateIn?: boolean;
  }) => (
    <div
      className={`amp-jaakko-tip${compact ? ' amp-jaakko-tip--compact' : ''}${whiteBackground ? ' amp-jaakko-tip--white' : ''}${animateIn ? ' amp-jaakko-tip--animate-in' : ''}`}
    >
      <img
        src={JAAKKO_AVATAR}
        alt=""
        aria-hidden="true"
        className="amp-jaakko-tip__avatar"
      />
      <p className="amp-jaakko-tip__text">
        <span className="amp-jaakko-tip__strong">{t(strongKey)}</span>
        {t(restKey)}
      </p>
    </div>
  );

  /**
   * Under the price field: the estimate tip while it is empty, the minimum
   * price error while it holds less than MIN_ASKING_PRICE (only once the field
   * is closed, so it does not flash while the seller is still typing), else
   * nothing.
   */
  const renderPriceExtra = () => {
    if (form.askingPrice.trim() === '') {
      return renderTip({
        strongKey: `${priceTipKeyPrefix()}Strong`,
        restKey: `${priceTipKeyPrefix()}Rest`,
        compact: true,
      });
    }
    if (priceTooLow && openField !== 'price') {
      return (
        <div
          ref={priceErrorRef}
          className="amp-detail-field__error"
          role="alert"
        >
          {t('newLandingPage.addMyPlace.details.priceTooLow')}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="amp-step amp-step--publish">
      <div className="amp-publish__scroll">
        <h2 className="amp-publish__title">
          {title ?? t('newLandingPage.addMyPlace.publish.title')}
        </h2>

        <div className="amp-listing-card">
          {onEditFacts && (
            <button
              type="button"
              className="amp-listing-card__edit-facts"
              onClick={onEditFacts}
              aria-label={t('common.edit', 'Edit')}
            >
              <EditIcon />
            </button>
          )}
          <div className="amp-listing-card__visual">
            {coverPhoto ? (
              <img
                src={coverPhoto.thumbnailUrl || coverPhoto.url}
                alt=""
                aria-hidden="true"
                className="amp-listing-card__cover"
                draggable={false}
              />
            ) : (
              <img
                src={HOUSE_IMG}
                alt=""
                aria-hidden="true"
                className="amp-listing-card__house"
              />
            )}
          </div>
          <div className="amp-listing-card__content">
            <p className="amp-listing-card__address">{`${displayAddress}, ${previewCity}`}</p>
            <p className="amp-listing-card__facts">
              <span className="amp-listing-card__size">
                {displaySize}m&sup2;
              </span>
              <span className="amp-listing-card__dot" aria-hidden="true" />
              <span className="amp-listing-card__rooms">
                {t('newLandingPage.addMyPlace.publish.roomsValue', {
                  rooms: displayRooms,
                })}
              </span>
            </p>
            {form.description.trim() !== '' && (
              <p className="amp-listing-card__description">
                {form.description}
              </p>
            )}
            {form.askingPrice.trim() !== '' ? (
              <p className="amp-listing-card__price">
                {`${formatThousands(form.askingPrice)} \u20AC`}
              </p>
            ) : (
              <div className="amp-listing-card__estimate">
                <span className="amp-listing-card__estimate-label">
                  <span className="amp-listing-card__estimating">
                    {completedEstimate ??
                      t('newLandingPage.addMyPlace.publish.estimatingPrice')}
                  </span>
                  <img
                    src={JAAKKO_AVATAR}
                    alt=""
                    aria-hidden="true"
                    className="amp-listing-card__estimate-avatar"
                  />
                </span>
                <EstimateInfoTooltip<HTMLButtonElement>
                  ariaLabel={t(
                    'newLandingPage.addMyPlace.publish.estimateInfoAria',
                  )}
                  content={t('newLandingPage.addMyPlace.publish.estimateInfo')}
                >
                  {(triggerProps) => (
                    <button
                      {...triggerProps}
                      type="button"
                      className="amp-listing-card__info"
                    >
                      <InfoIcon />
                    </button>
                  )}
                </EstimateInfoTooltip>
              </div>
            )}
          </div>
        </div>

        <div
          className={`amp-details${expanded ? ' amp-details--expanded' : ''}`}
        >
          <button
            type="button"
            className={`amp-details-toggle${expanded ? ' amp-details-toggle--expanded' : ''}`}
            onClick={() => onDetailsExpandedChange(!expanded)}
            aria-expanded={expanded}
          >
            <span className="amp-details-toggle__icon">
              <PlusIcon />
            </span>
            <span className="amp-details-toggle__text">
              <span className="amp-details-toggle__labels">
                <span className="amp-details-toggle__label amp-details-toggle__label--add">
                  {t('newLandingPage.addMyPlace.publish.addMoreDetails')}
                </span>
                <span className="amp-details-toggle__label amp-details-toggle__label--close">
                  {t('newLandingPage.addMyPlace.publish.closeDetails')}
                </span>
              </span>
              <span className="amp-details-toggle__hint-wrap">
                <span className="amp-details-toggle__hint">
                  {detailsHint ??
                    t('newLandingPage.addMyPlace.publish.addMoreDetailsHint')}
                </span>
              </span>
            </span>
          </button>

          {/*             <div className="amp-strength">
              <div className="amp-strength__top">
                <span className="amp-strength__label">
                  {t('newLandingPage.addMyPlace.details.listingStrength')}
                </span>
                <span className="amp-strength__value">{t('newLandingPage.addMyPlace.details.basic')}</span>
                <span className="amp-strength__chevron"><ChevronUpIcon /></span>
              </div>
              <div className="amp-strength__bar">
                <div className="amp-strength__fill" style={{ width: '28%' }} />
              </div>
              <div className="amp-strength__hint-row">
                <p className="amp-strength__hint">{t('newLandingPage.addMyPlace.details.strengthHint')}</p>
                <button type="button" className="amp-strength__add-photos">
                  {t('newLandingPage.addMyPlace.details.addPhotos')}
                </button>
              </div>
            </div> */}

          <div className="amp-details__content-wrap">
            <div className="amp-details__content">
              <h3 className="amp-details__heading">
                {t('newLandingPage.addMyPlace.details.listingDetails')}
              </h3>

              <div className="amp-details__list">
                <div className="amp-photos-group">
                  <input
                    ref={photoInputRef}
                    type="file"
                    className="amp-photo-input"
                    accept="image/*,.heic,.heif"
                    multiple
                    onChange={handlePhotoInputChange}
                  />
                  <AmpPhotoGrid
                    photos={photos}
                    onAddPhoto={handleAddPhoto}
                    onRemovePhoto={onRemovePhoto}
                    onReorderPhotos={onReorderPhotos}
                    onOpenPhotoManager={onOpenPhotoManager}
                    t={t}
                  />
                  {uploadError && (
                    <div className="amp-photos-error">{uploadError}</div>
                  )}
                  {photos.length === 0 &&
                    renderTip({
                      strongKey:
                        'newLandingPage.addMyPlace.details.photoTipStrong',
                      restKey: 'newLandingPage.addMyPlace.details.photoTipRest',
                    })}
                  {shouldShowEnhancedTip &&
                    enhancedTipVisible &&
                    renderTip({
                      strongKey:
                        'newLandingPage.addMyPlace.details.enhancedPhotoTipStrong',
                      restKey:
                        'newLandingPage.addMyPlace.details.enhancedPhotoTipRest',
                      whiteBackground: true,
                      animateIn: true,
                    })}
                </div>

                {renderOptionalField({
                  key: 'size',
                  fieldLabel: t('newLandingPage.addMyPlace.size.label'),
                  hasValue: form.size.trim() !== '',
                  displayValue: displaySize,
                  inline: true,
                  alwaysField: true,
                  renderEditing: () => (
                    <div className="amp-detail-field__box">
                      <input
                        className="amp-detail-field__input"
                        type="text"
                        inputMode="numeric"
                        placeholder={t(
                          'newLandingPage.addMyPlace.size.placeholder',
                        )}
                        value={form.size}
                        autoFocus
                        onChange={(event) =>
                          updateForm({
                            size: clampPropertySizeInput(event.target.value),
                          })
                        }
                      />
                      <button
                        type="button"
                        className="amp-detail-field__edit amp-detail-field__edit--button"
                        onClick={() => setOpenField(null)}
                        aria-label={t('newLandingPage.addMyPlace.size.label')}
                      >
                        <ClearIcon />
                      </button>
                    </div>
                  ),
                })}

                {renderOptionalField({
                  key: 'rooms',
                  fieldLabel: t('newLandingPage.addMyPlace.rooms.label'),
                  hasValue: form.rooms.trim() !== '',
                  displayValue: displayRooms,
                  alwaysField: true,
                  renderEditing: () => (
                    <div className="amp-pills amp-pills--scroll">
                      {['1', '2', '3', '4', '5+'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`amp-pill${form.rooms === option ? ' amp-pill--selected' : ''}`}
                          onClick={() => {
                            updateForm({ rooms: option });
                            setOpenField(null);
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  ),
                })}

                {renderOptionalField({
                  key: 'description',
                  emptyLabel: t(
                    'newLandingPage.addMyPlace.details.addDescription',
                  ),
                  fieldLabel: t(
                    'newLandingPage.addMyPlace.details.descriptionLabel',
                  ),
                  hasValue: form.description.trim() !== '',
                  displayValue: form.description,
                  clampValue: true,
                  renderEditing: () => (
                    <div className="amp-detail-field__textarea-wrap">
                      <div className="amp-detail-field__box amp-detail-field__box--textarea">
                        <textarea
                          className="amp-detail-field__textarea"
                          placeholder={t(
                            'newLandingPage.addMyPlace.details.descriptionPlaceholder',
                          )}
                          value={form.description}
                          autoFocus
                          maxLength={MAX_PROPERTY_DESCRIPTION_LENGTH}
                          onChange={(event) =>
                            updateForm({
                              description: event.target.value.slice(
                                0,
                                MAX_PROPERTY_DESCRIPTION_LENGTH,
                              ),
                            })
                          }
                        />
                      </div>
                      <span className="amp-detail-field__textarea-counter">
                        {form.description.length} /{' '}
                        {MAX_PROPERTY_DESCRIPTION_LENGTH}
                      </span>
                    </div>
                  ),
                })}

                {/*               <div className="amp-detail-field">
                <span className="amp-detail-field__label">{t('newLandingPage.addMyPlace.details.address')}</span>
                <div className="amp-detail-field__box amp-detail-field__box--stacked">
                  <div className="amp-detail-field__address">
                    <span className="amp-detail-field__value">{addressWithPostal}</span>
                    <span className="amp-detail-field__sub">
                      <LocationPinSmall />
                      {addressWithPostal}
                    </span>
                  </div>
                  <span className="amp-detail-field__edit"><EditIcon /></span>
                </div>
              </div> */}

                {!hidePrice &&
                  renderOptionalField({
                    key: 'price',
                    fieldLabel: t('newLandingPage.addMyPlace.details.price'),
                    hasValue: form.askingPrice.trim() !== '',
                    displayValue:
                      form.askingPrice.trim() !== ''
                        ? formatThousands(form.askingPrice)
                        : (completedEstimate ??
                          t(
                            'newLandingPage.addMyPlace.publish.estimatingPrice',
                          )),
                    alwaysField: true,
                    estimate: true,
                    renderExtra: renderPriceExtra,
                    renderEditing: () => (
                      <div className="amp-detail-field__box">
                        <input
                          className="amp-detail-field__input"
                          type="text"
                          inputMode="numeric"
                          placeholder={t(
                            'newLandingPage.addMyPlace.details.pricePlaceholder',
                          )}
                          value={formatThousands(form.askingPrice)}
                          autoFocus
                          onChange={(event) =>
                            updateForm({
                              askingPrice: event.target.value.replace(
                                /[^\d]/g,
                                '',
                              ),
                            })
                          }
                        />
                        <button
                          type="button"
                          className="amp-detail-field__edit amp-detail-field__edit--button"
                          onClick={() => setOpenField(null)}
                          aria-label={t(
                            'newLandingPage.addMyPlace.details.price',
                          )}
                        >
                          <ClearIcon />
                        </button>
                      </div>
                    ),
                  })}

                {renderOptionalField({
                  key: 'floor',
                  emptyLabel: t('newLandingPage.addMyPlace.details.addFloor'),
                  fieldLabel: t('newLandingPage.addMyPlace.details.floorLabel'),
                  hasValue: form.floor.trim() !== '',
                  displayValue: form.floor,
                  inline: true,
                  renderEditing: () => (
                    <div className="amp-detail-field__box">
                      <input
                        className="amp-detail-field__input"
                        type="text"
                        inputMode="numeric"
                        placeholder={t(
                          'newLandingPage.addMyPlace.details.floorPlaceholder',
                        )}
                        value={form.floor}
                        autoFocus
                        onChange={(event) =>
                          updateForm({
                            floor: clampPropertyFloorInput(event.target.value),
                          })
                        }
                      />
                      <button
                        type="button"
                        className="amp-detail-field__edit amp-detail-field__edit--button"
                        onClick={() => setOpenField(null)}
                        aria-label={t(
                          'newLandingPage.addMyPlace.details.floorLabel',
                        )}
                      >
                        <ClearIcon />
                      </button>
                    </div>
                  ),
                })}
                {renderOptionalField({
                  key: 'renovation',
                  emptyLabel: t(
                    'newLandingPage.addMyPlace.details.addRenovation',
                  ),
                  fieldLabel: t(
                    'newLandingPage.addMyPlace.details.renovationLabel',
                  ),
                  hasValue: form.renovationStatus !== '',
                  displayValue: labelFor(
                    renovationOptions,
                    form.renovationStatus,
                  ),
                  renderEditing: () =>
                    renderChoiceEditing(
                      renovationOptions,
                      form.renovationStatus,
                      (value) => updateForm({ renovationStatus: value }),
                      true,
                    ),
                })}
                {renderOptionalField({
                  key: 'elevator',
                  emptyLabel: t(
                    'newLandingPage.addMyPlace.details.hasElevator',
                  ),
                  fieldLabel: t(
                    'newLandingPage.addMyPlace.details.elevatorLabel',
                  ),
                  hasValue: form.hasElevator !== null,
                  displayValue: labelFor(yesNoOptions, form.hasElevator),
                  renderEditing: () =>
                    renderChoiceEditing(
                      yesNoOptions,
                      form.hasElevator,
                      (value) => updateForm({ hasElevator: value }),
                    ),
                })}
                {renderOptionalField({
                  key: 'balcony',
                  emptyLabel: t('newLandingPage.addMyPlace.details.hasBalcony'),
                  fieldLabel: t(
                    'newLandingPage.addMyPlace.details.balconyLabel',
                  ),
                  hasValue: form.hasBalcony !== null,
                  displayValue: labelFor(yesNoOptions, form.hasBalcony),
                  renderEditing: () =>
                    renderChoiceEditing(
                      yesNoOptions,
                      form.hasBalcony,
                      (value) => updateForm({ hasBalcony: value }),
                    ),
                })}

                {renderOptionalField({
                  key: 'type',
                  fieldLabel: t('newLandingPage.addMyPlace.details.type'),
                  hasValue: true,
                  displayValue: labelFor(buildingTypes, form.buildingType),
                  alwaysField: true,
                  renderEditing: () => (
                    <EdgeFadeScroller className="amp-pills">
                      {buildingTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          className={`amp-pill${form.buildingType === type.value ? ' amp-pill--selected' : ''}`}
                          onClick={() => {
                            onBuildingTypeChange(type.value);
                            setOpenField(null);
                          }}
                        >
                          {type.label}
                        </button>
                      ))}
                    </EdgeFadeScroller>
                  ),
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`amp-publish__footer${expanded ? ' amp-publish__footer--expanded' : ''}`}
      >
        <div className="amp-publish__card">
          {showEstimateNotify && (
            <div className="amp-publish__notify">
              <img
                src={JAAKKO_AVATAR}
                alt=""
                aria-hidden="true"
                className="amp-publish__notify-avatar"
              />
              <p className="amp-publish__notify-text">
                <strong>
                  {t('newLandingPage.addMyPlace.publish.estimateReadyStrong')}
                </strong>{' '}
                {t('newLandingPage.addMyPlace.publish.estimateReadyRest')}
              </p>
            </div>
          )}
          {/* Publishing while a photo is still uploading or being enhanced would
              commit the listing against an image that does not exist yet, so the
              CTA reads one busy state for both: it spins and stays unusable
              until the photo settles. Gated here rather than in each host so
              every screen that renders this step behaves alike: re-enhancing
              from the lightbox sets only isEnhancing, so isUploading alone would
              miss it. */}
          <button
            type="button"
            className="amp-cta amp-cta--publish"
            onClick={handlePublishClick}
            disabled={ctaBusy}
            aria-label={
              ctaBusy
                ? t('newLandingPage.addMyPlace.publish.publishing')
                : undefined
            }
          >
            {ctaBusy ? (
              <span className="amp-share__spinner" aria-hidden="true" />
            ) : (
              (ctaLabel ?? t('newLandingPage.addMyPlace.publish.publish'))
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReadyToPublishStep;
